import React from "react";
import { Calendar, ChevronRight, Expand, MapPin } from "lucide-react";
import BrandImage from "../common/BrandImage";
import MarkdownText from "../common/MarkdownText";
import DropPointsButton from "./DropPointsButton";
import EventVenueName from "./EventVenueName";
import JoinWhatsappGroupLink from "./JoinWhatsappGroupLink";
import RequestPickupButton from "./RequestPickupButton";
import {
  canRegisterForEvent,
  eventCardMedia,
  eventFeeLabel,
  eventPickupEnabled,
  eventRegistrationComingSoon,
  eventRegistrationNotRequired,
  NO_REGISTRATION_NEEDED_LABEL,
  publicDropPoints,
  publicWhatsappGroupUrl,
  REGISTRATIONS_OPENING_SOON_LABEL,
} from "../../utils/publicEventsApi";

const IST = "Asia/Kolkata";
const LOW_SPOTS_THRESHOLD = 10;

const primaryClass =
  "flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg bg-[var(--smym-accent)] px-3 py-2 text-center text-sm font-semibold text-[oklch(0.99_0.01_78)] transition duration-[220ms] hover:bg-[var(--smym-accent-hover)] active:scale-[0.98]";
const outlineClass =
  "flex min-h-11 w-full items-center justify-center gap-1.5 rounded-lg border border-[var(--smym-line)] bg-[var(--smym-paper)] px-3 py-2 text-center text-sm font-semibold text-[var(--smym-ink)] shadow-sm transition hover:border-[var(--smym-accent)] hover:text-[var(--smym-accent)]";
const secondaryClass =
  "smym-event-card__secondary inline-flex min-h-11 items-center justify-center gap-1.5 rounded-lg border border-transparent bg-[var(--smym-accent)] px-3 py-2 text-sm font-semibold text-[oklch(0.99_0.01_78)] transition duration-[220ms] hover:bg-[var(--smym-accent-hover)] active:scale-[0.98]";

function badgeClass(status) {
  switch (status) {
    case "Registrations Open":
      return "smym-event-badge smym-event-badge--open";
    case "Registrations Closed":
    case "Sold Out":
    case NO_REGISTRATION_NEEDED_LABEL:
    case REGISTRATIONS_OPENING_SOON_LABEL:
      return "smym-event-badge smym-event-badge--closed";
    default:
      return "smym-event-badge";
  }
}

function dateLabel(event) {
  if (event.date === "TBD" || event.date === "TBA") return event.date;
  const formatted = new Date(event.date).toLocaleDateString("en-IN", {
    timeZone: IST,
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
  return (event.endsAtEndOfDay ? "Until " : "") + formatted;
}

function CardMedia({ event, onViewFlyer }) {
  const media = eventCardMedia(event);
  if (!media.src) {
    return (
      <div className="smym-event-card__media smym-event-card__placeholder" aria-hidden>
        <BrandImage src="/smym-hd-no-bg.png?v=20261103" alt="" />
      </div>
    );
  }
  const photoAlt =
    media.fit === "cover" ? `${event.name} event image` : `${event.name} flyer`;
  const photo =
    media.fit === "cover" ? (
      <img src={media.src} alt={photoAlt} className="smym-event-card__photo" />
    ) : (
      <>
        <img src={media.src} alt="" aria-hidden className="smym-event-card__blur" />
        <img src={media.src} alt={photoAlt} className="smym-event-card__photo" />
      </>
    );
  const frameClass = `smym-event-card__media smym-event-card__media--${media.fit === "cover" ? "cover" : "flyer"}`;
  if (!media.flyer) return <div className={frameClass}>{photo}</div>;
  return (
    <button
      type="button"
      className={`smym-event-card__media-btn ${frameClass}`}
      aria-label={`View flyer for ${event.name}`}
      onClick={() => onViewFlyer(event)}
    >
      {photo}
      <span className="smym-event-card__flyer-pill" aria-hidden="true">
        <Expand size={14} />
        View flyer
      </span>
    </button>
  );
}

const UpcomingEventCard = ({ event, onViewFlyer, onRegisterEvent }) => {
  const notRequired = eventRegistrationNotRequired(event);
  const comingSoon = eventRegistrationComingSoon(event);
  const status = notRequired ? NO_REGISTRATION_NEEDED_LABEL : event.status;
  const showRegister = canRegisterForEvent(event) && Boolean(onRegisterEvent);
  const groupUrl = publicWhatsappGroupUrl(event);
  const showDrop = publicDropPoints(event).length > 0;
  const showPickup = eventPickupEnabled(event);
  const fee = eventFeeLabel(event);
  const live = status === "Registrations Open" && !notRequired;

  return (
    <article className="smym-event-card smym-event-card--upcoming">
      <CardMedia event={event} onViewFlyer={onViewFlyer} />
      <div className="flex flex-1 flex-col gap-3 p-4">
        <h4 className="text-xl font-bold leading-snug text-[var(--smym-ink)]">
          {event.name}
        </h4>
        <div className="space-y-1 text-sm text-[var(--smym-ink-muted)]">
          <div className="flex items-center">
            <Calendar className="mr-2 h-4 w-4 shrink-0" aria-hidden />
            <span>
              {dateLabel(event)}
              {event.time ? ` · ${event.time}` : ""}
            </span>
          </div>
          {event.venue ? (
            <div className="flex items-center">
              <MapPin className="mr-2 h-4 w-4 shrink-0" aria-hidden />
              <span>
                <EventVenueName event={event} />
              </span>
            </div>
          ) : null}
        </div>
        {event.description ? (
          <div className="smym-event-card__desc">
            <MarkdownText>{event.description}</MarkdownText>
          </div>
        ) : null}
        {event.sponsors?.length > 0 ? (
          <div className="smym-event-sponsors">
            <span className="smym-event-sponsors__label">Sponsored by</span>{" "}
            {event.sponsors.map((sponsor, index) => (
              <React.Fragment key={`${sponsor}-${index}`}>
                {index > 0 ? " & " : null}
                <MarkdownText inline>{sponsor}</MarkdownText>
              </React.Fragment>
            ))}
          </div>
        ) : null}
        {event.specialMention ? (
          <div className="smym-event-sponsors">
            <span className="smym-event-sponsors__label">Special mention</span>{" "}
            <MarkdownText inline>{event.specialMention}</MarkdownText>
          </div>
        ) : null}
        {fee ? (
          <p className="text-sm font-semibold text-[var(--smym-ink)]">{fee}</p>
        ) : null}
        <div className="mt-auto flex flex-col gap-2">
          {comingSoon ? null : (
            <div>
              <span className={badgeClass(status)}>
                {live ? <span className="smym-event-badge__dot" aria-hidden /> : null}
                {status}
              </span>
            </div>
          )}
          {showRegister &&
          event.remaining != null &&
          event.remaining <= LOW_SPOTS_THRESHOLD ? (
            <p className="text-xs font-semibold text-[var(--smym-accent)]">
              {event.remaining === 1
                ? "Only 1 spot left"
                : `Only ${event.remaining} spots left`}
            </p>
          ) : null}
          {comingSoon ? (
            <p
              role="status"
              className="rounded-lg bg-[oklch(0.545_0.145_48)] px-4 py-2 text-center text-sm font-semibold uppercase tracking-wide text-white"
            >
              {REGISTRATIONS_OPENING_SOON_LABEL}
            </p>
          ) : null}
          {showRegister ? (
            <button
              type="button"
              className={primaryClass}
              onClick={() => onRegisterEvent(event)}
            >
              Register
              <ChevronRight className="h-4 w-4" aria-hidden />
            </button>
          ) : null}
          {groupUrl ? (
            <JoinWhatsappGroupLink
              event={event}
              className={showRegister ? outlineClass : primaryClass}
            />
          ) : null}
          {showDrop || showPickup ? (
            <div className="flex flex-wrap gap-2">
              {showDrop ? (
                <DropPointsButton event={event} className={secondaryClass} />
              ) : null}
              {showPickup ? (
                <RequestPickupButton event={event} className={secondaryClass} />
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </article>
  );
};

export default UpcomingEventCard;
